import { Ellipsis, Menu, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import ProfilePicture from '../ui/profile-picture';
import { IUser } from '@/redux/auth/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import LoginDialog from './LoginDialog';

interface MobileNavProps {
  isAuthenticated: boolean;
  user: IUser | null;
  onLogout: () => void;
}

function MobileNav({ isAuthenticated, user, onLogout }: MobileNavProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="text-white md:hidden">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:w-[400px] p-0">
        <SheetHeader className="p-4 border-b text-left">
          <SheetTitle className="text-lg font-bold">Filefinity</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col p-6 justify-between h-[93vh]">
          <nav className="flex flex-col h-full justify-between">
            <div className="flex flex-col space-y-4">
              <a
                href="https://filefinity.featurebase.app/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSheetOpen(false)}
                className="text-lg font-medium transition-colors hover:text-primary"
              >
                News
              </a>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/myprojects"
                    onClick={() => setSheetOpen(false)}
                    className="text-lg font-medium transition-colors hover:text-primary"
                  >
                    My Projects
                  </Link>
                  <Link
                    to={`/${user?.username}`}
                    onClick={() => setSheetOpen(false)}
                    className="text-lg font-medium transition-colors hover:text-primary"
                  >
                    Profile
                  </Link>
                </>
              ) : (
                <div />
              )}
            </div>
            <div className="flex flex-col space-y-4 py-4">
              {isAuthenticated ? (
                <Link to="/project/create">
                  <Button
                    size="lg"
                    variant="outline"
                    className="mt-4 border-gradient border-2 w-full justify-between"
                  >
                    Upload Project
                    <Upload />
                  </Button>
                </Link>
              ) : (
                <>
                  <LoginDialog variant="default" />
                  <Link to="/signup">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-gradient border-2 w-full"
                    >
                      Sign up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>

          {isAuthenticated && (
            <div className="flex items-center pt-6 border-t justify-between">
              <Link
                to={`/${user?.username}`}
                onClick={() => setSheetOpen(false)}
              >
                <div className="flex items-center space-x-4">
                  <ProfilePicture
                    src={user?.profilePicture}
                    alt={user?.firstname}
                    className="h-10 w-10 border-2"
                  />
                  <p className="font-semibold">@{user?.username}</p>
                </div>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="border px-1 rounded-sm">
                    <Ellipsis />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 m-3"
                  onClick={() => setSheetOpen(false)}
                >
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to={`/${user?.username}`}>Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onLogout}>
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default MobileNav;
